import type { User } from '@auth0/auth0-react';
import type { Private } from '@seek/indie-api-types';
import { Inline, Stack, Text } from 'braid-design-system';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router';

import { useApi } from '#src/api/index.ts';
import { ErrorNotice } from '#src/components/ErrorNotice/ErrorNotice.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';

import type { FormValues } from '../../SecurityBreach.tsx';

import { ActionsTaken } from './components/ActionsTaken/ActionsTaken.tsx';
import { ClearForm } from './components/Clear/Clear.tsx';
import { ContactInfo } from './components/ContactInfo/ContactInfo.tsx';
import { DataType } from './components/DataType/DataType.tsx';
import { ExtraDetails } from './components/ExtraDetails/ExtraDetails.tsx';
import { LoginPrompt } from './components/LoginPrompt/LoginPrompt.tsx';
import { ProductsImpacted } from './components/ProductsImpacted/ProductsImpacted.tsx';
import { ResultLabel } from './components/ResultLabel/ResultLabel.tsx';
import { SaveFormValues } from './components/SaveFormValues/SaveFormValues.tsx';
import { SeekData } from './components/SeekData/SeekData.tsx';
import { StartDate } from './components/Dates/StartDate.tsx';
import { Submit } from './components/Submit/Submit.tsx';
import { TerminationConfirmationDialog } from './components/TerminationConfirmation/TerminationConfirmation.tsx';
import { ThanksView } from './components/ThanksView/ThanksView.tsx';
import { UsersImpacted } from './components/UsersImpacted/UsersImpacted.tsx';
import { Disclosure } from './components/Disclosure/Disclosure.tsx';
import { TurnstileWidget } from '#src/components/TurnstileWidget/TurnstileWidget.tsx';
import { useEnvironmentConfig } from '#src/hooks/environment.tsx';
import { ResolvedDate } from './components/Dates/ResolvedDate.tsx';

type SetBooleanState = React.Dispatch<React.SetStateAction<boolean>>;

type State = {
  setSubmitted: SetBooleanState;
  setOpenIntegrationTerminationDialog: SetBooleanState;
  setRequestError: SetBooleanState;
};

type Apis = {
  authenticated: ReturnType<typeof useApi>;
  unauthenticated: ReturnType<typeof useApi>;
};

const submit = async (
  values: FormValues,
  { setSubmitted, setOpenIntegrationTerminationDialog, setRequestError }: State,
  apis: Apis,
  terminateIntegration: boolean,
  token?: string,
) => {
  const report: Private.SecurityBreachInput = {
    ...values,
    productsImpacted: [
      values.productsImpacted[0],
      ...values.productsImpacted.slice(1),
    ],
    incidentStartTimeUTC: getUTCDate(values.startDate),
    incidentResolvedTimeUTC: values.resolvedDate
      ? getUTCDate(values.resolvedDate)
      : undefined,
    seekData: values.seekData === 'Yes',
    seekCredentials: values.seekCredentials === 'Yes',
    seekFault: values.seekFault === 'Yes',
    actionTaken: values.actionTaken === 'Yes',
    partnerInfo: {
      partnerName: values.partnerName,
      contactEmail: values.contactEmail,
      contactName: values.contactName,
      contactPhone: values.contactPhone,
      contactHours: values.contactHours,
    },
    numberImpacted: values.usersImpacted,
    disclosure: values.disclosure,
  };

  try {
    if (terminateIntegration) {
      await apis.authenticated.securityBreach.reportAuthenticated(report);
    } else {
      await apis.unauthenticated.securityBreach.reportUnauthenticated({
        ...report,
        token,
      });
    }
    setSubmitted(true);
    setOpenIntegrationTerminationDialog(false);
  } catch {
    setRequestError(true);
  }
};

const getUTCDate = (date: string) => {
  const dateObj = new Date(date);
  const utcDate = new Date(
    dateObj.getTime() + new Date().getTimezoneOffset() * 60000,
  );
  return utcDate.toISOString();
};

interface SecurityBreachFormProps {
  defaultValues: FormValues;
  user: User | undefined;
}

export const SecurityBreachForm = ({
  defaultValues,
  user,
}: SecurityBreachFormProps) => {
  const authenticated = useApi();
  const unauthenticated = useApi({ dangerouslyDefeatAuth: true });
  const [submitted, setSubmitted] = useState(false);
  const [
    openIntegrationTerminationDialog,
    setOpenIntegrationTerminationDialog,
  ] = useState(false);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [confirmTermination, setConfirmTermination] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(
    undefined,
  );
  const [missingTokenError, setMissingTokenError] = useState(false);

  const { pathname } = useLocation();
  const { isImpersonating } = usePermissions();

  const localFormValues = localStorage.getItem('securityBreachFormValues');
  const { enableTurnstile } = useEnvironmentConfig();

  let values;
  try {
    values = localFormValues ? JSON.parse(localFormValues) : defaultValues;
  } catch {
    localStorage.removeItem('securityBreachFormValues');
    values = defaultValues;
  }

  const methods = useForm<FormValues>({
    values,
    reValidateMode: 'onChange',
  });
  const { handleSubmit, getValues } = methods;

  if (!user && !openLoginDialog) {
    setOpenLoginDialog(true);
  }

  const onValid = () => {
    if (enableTurnstile && !user && !turnstileToken) {
      setMissingTokenError(true);
      return;
    }

    setMissingTokenError(false);

    if (terminateIntegration) {
      setOpenIntegrationTerminationDialog(true);
      return;
    }

    submit(
      getValues(),
      {
        setSubmitted,
        setOpenIntegrationTerminationDialog,
        setRequestError,
      },
      { authenticated, unauthenticated },
      terminateIntegration,
      turnstileToken,
    );
  };

  const terminateIntegration: boolean =
    (getValues('seekData') === 'Yes' ||
      getValues('seekCredentials') === 'Yes' ||
      getValues('seekFault') === 'Yes') &&
    Boolean(user) &&
    !isImpersonating;

  return (
    <>
      {!submitted ? (
        <form onSubmit={handleSubmit(onValid)}>
          <FormProvider {...methods}>
            <Stack space="small">
              <SaveFormValues>
                <Stack space="medium">
                  <DataType />
                  <UsersImpacted />
                  <StartDate />
                  <ResolvedDate />
                  <ActionsTaken />
                  <ProductsImpacted />
                  <SeekData />
                  <Disclosure />
                  <ContactInfo />
                  <ExtraDetails />
                </Stack>
              </SaveFormValues>
              {enableTurnstile && !user && (
                <TurnstileWidget
                  onTurnstileValidate={setTurnstileToken}
                  missingTokenError={missingTokenError}
                />
              )}
              {missingTokenError && (
                <Text tone="critical" size="xsmall">
                  Please complete the verification challenge to proceed.
                </Text>
              )}
              {requestError && (
                <ErrorNotice
                  title="Error submitting request"
                  description="There was a problem submitting your request. Try again."
                />
              )}
              <ResultLabel terminateIntegration={terminateIntegration} />
              <Inline space="small">
                <Submit />
                <ClearForm />
              </Inline>
            </Stack>
          </FormProvider>
        </form>
      ) : (
        <ThanksView
          formValues={getValues()}
          terminateIntegration={terminateIntegration}
        />
      )}
      <TerminationConfirmationDialog
        open={openIntegrationTerminationDialog}
        setOpen={setOpenIntegrationTerminationDialog}
        confirmTermination={confirmTermination}
        setConfirmTermination={setConfirmTermination}
        submit={() =>
          submit(
            getValues(),
            {
              setSubmitted,
              setOpenIntegrationTerminationDialog,
              setRequestError,
            },
            { authenticated, unauthenticated },
            terminateIntegration,
          )
        }
        values={getValues()}
      />
      <LoginPrompt
        open={openLoginDialog}
        setOpen={setOpenLoginDialog}
        redirect={pathname}
      />
    </>
  );
};
